import type { FunctionComponent } from "preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../../components/icon/icon.tsx";
import { CompareMode, useComparePhotoCanvas } from "./compare_photo_canvas.ts";
import { PhotoSelectDialog } from "../../components/photo_select_dialog/photo_select_dialog.tsx";
import type { PhotoInfo } from "../../store/task/photos.ts";
import { useLocation } from "wouter-preact";
import { PhotoTimeline } from "./photo_timeline.tsx";
import { useStore } from "@nanostores/preact";
import { $cameraList, $cameraListLoaded, refreshCameraPage } from "../../store/cameras.ts";
import { $isAdvanceUser, $isLogined, $sessionVersion } from "../../store/user_info.ts";
import { usePageRefresh } from "../../hooks/use_page_refresh.ts";
import { useToast } from "../../components/toast/toast.tsx";

export const ComparePhotoPage: FunctionComponent = () => {
    const [selectPhotoFor, setSelectPhotoFor] = useState(0);
    const [openSelectPhotoDialog, setOpenSelectPhotoDialog] = useState(false);
    const [lastCameraId, setLastCameraId] = useState(-1);
    const [currentMode, setCurrentMode] = useState(CompareMode.Full);
    const [selectedLeftPhotoId, setSelectedLeftPhotoId] = useState<number | null>(null);
    const [selectedRightPhotoId, setSelectedRightPhotoId] = useState<number | null>(null);
    const canvasObject = useComparePhotoCanvas();
    const t = useTranslator();
    const toast = useToast();
    const isAdvanceUser = useStore($isAdvanceUser);
    const [_, setLocation] = useLocation();
    const initAppliedRef = useRef(false);
    const cameraHistoryAppliedRef = useRef(false);
    const lastCameraIdPrevRef = useRef<number | null>(null);
    
    const cameraList = useStore($cameraList);
    const cameraListLoaded = useStore($cameraListLoaded);
    const isLogined = useStore($isLogined);
    const sessionVersion = useStore($sessionVersion);
    const refresh = usePageRefresh({ pageKey: "compare", enabled: isLogined, refresh: refreshCameraPage });
    useEffect(() => {
        return () => {
            canvasObject.clearImages();
            setSelectedLeftPhotoId(null);
            setSelectedRightPhotoId(null);
            setLastCameraId(-1);
        };
    }, [sessionVersion]);

    const preventDefaultHandler = useCallback((e: Event) => {
        e.preventDefault();
    }, []);

    const closeSelectPhotoDialogCallback = useCallback(() => {
        setOpenSelectPhotoDialog(false);
    }, [setOpenSelectPhotoDialog]);

    const selectPhotoCallback = useCallback((cameraId: number, photo: PhotoInfo) => {
        if (cameraId !== lastCameraId) {
            canvasObject.clearImages();
            setSelectedLeftPhotoId(null);
            setSelectedRightPhotoId(null);
            lastCameraIdPrevRef.current = cameraId;
        }
        setLastCameraId(cameraId);
        setOpenSelectPhotoDialog(false);
        canvasObject.setImage(selectPhotoFor, photo.photo_id, photo.photo_time);
        if (selectPhotoFor === 0) setSelectedLeftPhotoId(photo.photo_id);
        if (selectPhotoFor === 1) setSelectedRightPhotoId(photo.photo_id);
    }, [setOpenSelectPhotoDialog, canvasObject.setImage, canvasObject.clearImages, selectPhotoFor, lastCameraId]);

    const actionModeHorizontal = useCallback(() => {
        canvasObject.setCompareMode(CompareMode.Horizontal);
        setCurrentMode(CompareMode.Horizontal);
    }, [canvasObject.setCompareMode]);

    const actionModeArea = useCallback(() => {
        canvasObject.setCompareMode(CompareMode.Area);
        setCurrentMode(CompareMode.Area);
    }, [canvasObject.setCompareMode]);

    const actionModeVertical = useCallback(() => {
        canvasObject.setCompareMode(CompareMode.Vertical);
        setCurrentMode(CompareMode.Vertical);
    }, [canvasObject.setCompareMode]);

    const actionModeFull = useCallback(() => {
        canvasObject.setCompareMode(CompareMode.Full);
        setCurrentMode(CompareMode.Full);
    }, [canvasObject.setCompareMode]);

    useEffect(() => {
        document.title = t({
            zh: "照片对比",
            en: "Compare Photo",
        });
    }, [t]);

    useEffect(() => {
        if (!cameraListLoaded || lastCameraId <= 0 || cameraList.some(c => c.camera_id === lastCameraId)) return;
        canvasObject.clearImages();
        setSelectedLeftPhotoId(null);
        setSelectedRightPhotoId(null);
        setLastCameraId(-1);
        toast(t({ zh: "当前相机已移除或无权访问", en: "Camera removed or access denied" }), "warn");
    }, [cameraList, cameraListLoaded]);

    useEffect(() => {
        // set selected camera from outside or default to first camera
        if (!cameraHistoryAppliedRef.current && globalThis.history?.state?.cameraId) {
            cameraHistoryAppliedRef.current = true;
            const cameraId = Number.parseInt(globalThis.history?.state?.cameraId) ?? Number.NaN;
            if (!isNaN(cameraId)) {
                console.log("history:", cameraId);
                setLastCameraId(cameraId);
                return;
            }
        }
        
        // If no history state, and we have cameras, select the first one if not set
        if (lastCameraId === -1 && cameraList.length > 0) {
            setLastCameraId(cameraList[0].camera_id);
        }
    }, [cameraList, lastCameraId]);

    useEffect(() => {
        const prev = lastCameraIdPrevRef.current;
        lastCameraIdPrevRef.current = lastCameraId;
        if (prev === lastCameraId) return;
        if (prev === null) return;
        if (prev === -1) return;
        canvasObject.clearImages();
        setSelectedLeftPhotoId(null);
        setSelectedRightPhotoId(null);
    }, [lastCameraId, canvasObject.clearImages]);

    useEffect(() => {
        if (initAppliedRef.current) return;
        const state: any = globalThis.history?.state ?? {};
        const photoIdRaw = state.photoId ?? state.photo_id ?? state.photoID;
        const photoTimeRaw = state.photoTime ?? state.photo_time ?? state.photoTIME;
        const photoId = Number.parseInt(String(photoIdRaw ?? ""));
        const photoTime = Number.parseInt(String(photoTimeRaw ?? ""));
        if (!Number.isFinite(photoId) || photoId <= 0) return;
        if (!Number.isFinite(photoTime) || photoTime <= 0) return;
        const sideRaw = state.compareSide ?? state.side ?? "L";
        const side = sideRaw === "R" || sideRaw === 1 ? 1 : 0;
        canvasObject.setImage(side, photoId, photoTime);
        setSelectPhotoFor(side);
        if (side === 0) setSelectedLeftPhotoId(photoId);
        if (side === 1) setSelectedRightPhotoId(photoId);
        initAppliedRef.current = true;
    }, [canvasObject.setImage, setSelectPhotoFor]);

    useEffect(() => {
        // Initial render
        // The canvas hook handles resize events now
        canvasObject.render();
    }, [canvasObject.render]);

    return (
        <div class="farpov_cmp_photo" ref={canvasObject.pointerAreaRef} style={{ display: 'flex', flexDirection: 'column' }}>
             {refresh.error && <p role="alert">{refresh.error}</p>}
             {/* 1. Top Header */}
            <div class="farpov_cmp_header">
                <div class="farpov_cmp_title">
                    {/* Camera Selector */}
                    {cameraList.length > 0 && (
                        <div style={{ position: "relative" }}>
                            <select 
                                value={lastCameraId} 
                                onChange={(e: Event) => setLastCameraId(Number((e.target as HTMLInputElement).value))}
                                style={{ 
                                    background: "var(--pico-card-background-color)", 
                                    color: "var(--pico-color)", 
                                    borderColor: "var(--pico-muted-border-color)", 
                                    borderRadius: "4px",
                                    padding: "0.2rem 2rem 0.2rem 0.5rem",
                                    fontSize: "0.9rem",
                                    cursor: "pointer",
                                    maxWidth: "200px",
                                    marginBottom: 0,
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                {cameraList.map(cam => (
                                    <option key={cam.camera_id} value={cam.camera_id}>
                                        {cam.camera_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                
                <div class="farpov_cmp_modes">
                    <button 
                        class={`farpov_cmp_mode_btn ${currentMode === CompareMode.Full ? "active" : ""}`}
                        onClick={actionModeFull}
                    >
                        {t({ zh: "叠加", en: "Overlay" })}
                    </button>
                    <button 
                        class={`farpov_cmp_mode_btn ${currentMode === CompareMode.Horizontal ? "active" : ""}`}
                        onClick={actionModeHorizontal}
                    >
                        {t({ zh: "左右", en: "H-Slider" })}
                    </button>
                    <button 
                        class={`farpov_cmp_mode_btn ${currentMode === CompareMode.Vertical ? "active" : ""}`}
                        onClick={actionModeVertical}
                    >
                        {t({ zh: "上下", en: "V-Slider" })}
                    </button>
                     <button 
                        class={`farpov_cmp_mode_btn ${currentMode === CompareMode.Area ? "active" : ""}`}
                        onClick={actionModeArea}
                    >
                        {t({ zh: "区域", en: "Area" })}
                    </button>
                </div>
                
                <div class="farpov_cmp_spacer"></div>
            </div>

            {/* 2. Main Safezone (Canvas Overlay) */}
            <div class="farpov_cmp_ph_safezone_v2" ref={canvasObject.safezoneRef}>
                <canvas
                    class="farpov_cmp_canvas"
                    ref={canvasObject.canvasRef}
                    onContextMenu={preventDefaultHandler}
                    onContextMenuCapture={preventDefaultHandler}
                >
                </canvas>
                {/* Empty State / Hint */}
                {(!canvasObject.hasImage1 && !canvasObject.hasImage2) && (
                    <div style={{ 
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        display: "flex", 
                        flexDirection: "column", 
                        alignItems: "center", 
                        justifyContent: "center", 
                        height: "100%", 
                        color: "var(--pico-muted-color)",
                        pointerEvents: "none",
                        zIndex: 10
                    }}>
                        <Icon name="image" style={{ width: "64px", height: "64px", marginBottom: "1rem", opacity: 0.5 }} />
                        <p style={{ fontSize: "1.2rem", fontWeight: "bold", margin: 0 }}>
                            {t({ zh: "请选择照片开始对比", en: "Select photos to compare" })}
                        </p>
                        <p style={{ fontSize: "0.9rem", marginTop: "0.5rem" }}>
                            {t({ zh: "在下方时间轴选择照片", en: "Select from timeline below" })}
                        </p>
                    </div>
                )}
                
                {/* Date Overlays */}
                {canvasObject.isBusy && (
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 30,
                        pointerEvents: "none"
                    }}>
                        <div style={{ 
                            padding: "0.5rem 1rem", 
                            background: "rgba(0, 0, 0, 0.6)", 
                            color: "white",
                            borderRadius: "20px",
                            backdropFilter: "blur(4px)",
                            fontSize: "0.9rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem"
                        }}>
                             <span aria-busy="true"></span>
                             {t({ zh: "加载中...", en: "Loading..." })}
                        </div>
                    </div>
                )}
                {canvasObject.hasImage1 && canvasObject.image1Time > 0 && (
                     <div style={{
                         position: "absolute",
                         top: "1rem",
                         left: "1rem",
                         background: "rgba(0,0,0,0.5)",
                         color: "white",
                         padding: "0.2rem 0.5rem",
                         borderRadius: "4px",
                         fontSize: "0.85rem",
                         pointerEvents: "none",
                         zIndex: 20
                     }}>
                         Left: {new Date(canvasObject.image1Time * 1000).toLocaleString()}
                     </div>
                )}
                
                {canvasObject.hasImage2 && canvasObject.image2Time > 0 && (
                     <div style={{
                         position: "absolute",
                         top: "1rem",
                         right: "1rem",
                         background: "rgba(0,0,0,0.5)",
                         color: "white",
                         padding: "0.2rem 0.5rem",
                         borderRadius: "4px",
                         fontSize: "0.85rem",
                         pointerEvents: "none",
                         zIndex: 20
                     }}>
                         Right: {new Date(canvasObject.image2Time * 1000).toLocaleString()}
                     </div>
                )}
            </div>

            {/* 3. Bottom Timeline */}
            <PhotoTimeline 
                cameraId={lastCameraId}
                selectedLeftPhotoId={selectedLeftPhotoId ?? undefined}
                selectedRightPhotoId={selectedRightPhotoId ?? undefined}
                onSelectPhoto={async (photo, target) => {
                     if (target === 1 && !isAdvanceUser) {
                         toast(t({ zh: "不是高级用户，无法使用", en: "Advance user required" }), "error");
                         return;
                     }
                     try {
                        // Assuming target 0 is image1 (Left/Bottom), 1 is image2 (Right/Top)
                        // Pass photo.photo_time to setImage
                        canvasObject.setImage(target, photo.photo_id, photo.photo_time);
                        if (target === 0) setSelectedLeftPhotoId(photo.photo_id);
                        if (target === 1) setSelectedRightPhotoId(photo.photo_id);
                    } catch (e) {
                        console.error("Failed to set image", e);
                    }
                }}
            />

            <PhotoSelectDialog
                isOpen={openSelectPhotoDialog}
                cameraId={lastCameraId}
                onCancel={closeSelectPhotoDialogCallback}
                onPhotoSelected={selectPhotoCallback}
            />
        </div>
    );
};
export default ComparePhotoPage;
